/**
 * Wave 3.53 — Generic searchable single-select combobox.
 *
 * Drop-in replacement for the common Shadcn ``<Select>`` pattern when
 * the list of options grows beyond ~10 entries. Adds a type-ahead
 * search input that filters by any configurable set of fields on the
 * item objects.
 *
 * ─── Usage ───────────────────────────────────────────────────────────
 *   <SearchableSelect
 *     items={classifications}
 *     value={form.classification_id}
 *     onValueChange={(v, item) => setForm({ ...form, classification_id: v })}
 *     itemToValue={(c) => c.id}
 *     itemToLabel={(c) => `${c.schema_code} — ${c.shape_name}`}
 *     searchKeys={['schema_code', 'shape_name']}
 *     placeholder="Select shape…"
 *     testId="packet-classification-select"
 *   />
 *
 * The component intentionally mirrors the ``<Select>`` API
 * (``value`` + ``onValueChange``) so it can be dropped in with minimal
 * caller-side changes. Test IDs follow the standard portal convention:
 *
 *   - ``<testId>``                — trigger button
 *   - ``<testId>-search-input``   — search box
 *   - ``<testId>-clear``          — clear (X) icon when ``allowClear``
 *   - ``<testId>-option-<value>`` — each row in the list
 *   - ``<testId>-empty``          — empty / no-match state
 *
 * Why not Shadcn's ``<Command>`` directly? — The Command API uses an
 * intrinsic search filter that is brittle for our composite labels;
 * implementing our own filter keeps the behaviour explicit, makes
 * ``searchKeys`` trivial, and matches the existing CustomerCombobox.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronsUpDown, Check, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const noop = () => {};

/**
 * @param {object} props
 * @param {Array<any>} props.items          List of option objects.
 * @param {string|number|null} props.value  Currently-selected value.
 * @param {(value: any, item: any) => void} props.onValueChange
 * @param {(item: any) => any} [props.itemToValue]   Defaults to ``item.id``.
 * @param {(item: any) => React.ReactNode} [props.itemToLabel]
 *        Defaults to ``item.name``. Receives the full item for richer
 *        rendering (badges, secondary text, …).
 * @param {(item: any) => React.ReactNode} [props.itemToSubLabel]
 *        Optional second-line content shown beneath the main label.
 * @param {string[]} [props.searchKeys]     Field names to search against.
 *        Defaults to common keys. May be a function ``(item) => string``
 *        for fully-custom needles.
 * @param {string} [props.placeholder]
 * @param {string} [props.searchPlaceholder]
 * @param {boolean} [props.allowClear]    Show an "X" icon on the trigger to
 *        clear the current selection. Defaults to **true** (Wave 3.53 fix
 *        — users asked for a way to deselect without refreshing the page).
 *        Pass ``allowClear={false}`` on required form fields where you do
 *        not want users to be able to clear the selection.
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.loading]
 * @param {string} [props.emptyText]
 * @param {string} [props.noMatchText]
 * @param {React.ReactNode} [props.leadingIcon]  Icon shown inside trigger.
 * @param {string} [props.triggerClassName]
 * @param {string} [props.contentClassName]
 * @param {string} [props.testId]
 * @param {number} [props.maxRows]          Max rows to render at once.
 * @param {React.ReactNode} [props.footer]  Optional footer (e.g. "+ Add new").
 */
export function SearchableSelect({
  items = [],
  value,
  onValueChange = noop,
  itemToValue,
  itemToLabel,
  itemToSubLabel,
  searchKeys,
  placeholder = 'Select…',
  searchPlaceholder = 'Type to search…',
  allowClear = true,
  disabled = false,
  loading = false,
  emptyText = 'No options available.',
  noMatchText = 'No matches.',
  leadingIcon = null,
  triggerClassName = '',
  contentClassName = '',
  testId = 'searchable-select',
  maxRows = 200,
  footer = null,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef(null);

  const getValue = itemToValue || ((it) => (it && (it.id ?? it.value ?? it.code)) ?? '');
  const getLabel = itemToLabel || ((it) => (it && (it.name ?? it.label ?? String(getValue(it)))) || '');

  const selected = useMemo(
    () => items.find((it) => String(getValue(it)) === String(value)) || null,
    [items, value, getValue],
  );

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      let needles = [];
      if (typeof searchKeys === 'function') {
        needles = [searchKeys(it)];
      } else if (Array.isArray(searchKeys) && searchKeys.length > 0) {
        needles = searchKeys.map((k) => (it && it[k] != null ? String(it[k]) : ''));
      } else {
        // Sensible defaults — common identifier fields.
        needles = [
          it?.schema_code, it?.shape_name, it?.name, it?.code, it?.label,
          it?.packet_no, it?.purity_name, it?.email, it?.phone,
        ].filter((x) => x != null).map(String);
      }
      // Also include the visible label for "what-you-see-is-what-you-search".
      try {
        const visible = getLabel(it);
        if (typeof visible === 'string') needles.push(visible);
      } catch { /* ignore */ }
      const hay = needles.join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [items, query, searchKeys, getLabel]);

  // Reset the query whenever the popover closes so re-opening is fresh.
  useEffect(() => { if (!open) setQuery(''); }, [open]);

  const pick = (it) => {
    onValueChange(getValue(it), it);
    setOpen(false);
  };

  const clear = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onValueChange('', null);
    setOpen(false);
  };

  const labelNode = selected ? getLabel(selected) : placeholder;

  return (
    <Popover open={open && !disabled} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal h-9',
            !selected && 'text-slate-500',
            triggerClassName,
          )}
          data-testid={testId}
        >
          <span className="flex items-center gap-2 min-w-0 flex-1 text-left">
            {leadingIcon ? (
              <span className="flex-shrink-0 text-slate-400">{leadingIcon}</span>
            ) : null}
            <span className="truncate">{labelNode}</span>
          </span>
          <span className="flex items-center gap-1 flex-shrink-0">
            {selected && allowClear && !disabled && (
              <span
                role="button"
                tabIndex={0}
                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={clear}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); clear(e); } }}
                data-testid={`${testId}-clear`}
                aria-label="Clear selection"
                className="inline-flex items-center justify-center text-slate-400 hover:text-rose-600 cursor-pointer rounded-sm focus:outline-none focus:ring-1 focus:ring-rose-300"
              >
                <X size={14} />
              </span>
            )}
            <ChevronsUpDown size={14} className="text-slate-400" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'p-0 w-[var(--radix-popover-trigger-width)] max-w-[480px]',
          contentClassName,
        )}
        align="start"
      >
        <div className="border-b border-[var(--divider)] p-2">
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-8 text-sm"
            data-testid={`${testId}-search-input`}
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-xs text-slate-500">
              <Loader2 size={14} className="animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div
              className="px-3 py-4 text-center text-xs text-slate-500"
              data-testid={`${testId}-empty`}
            >
              {items.length === 0 ? emptyText : noMatchText}
            </div>
          ) : (
            filtered.slice(0, maxRows).map((it) => {
              const v = getValue(it);
              const isSel = String(v) === String(value);
              return (
                <button
                  key={String(v) || Math.random()}
                  type="button"
                  onClick={() => pick(it)}
                  className={cn(
                    'w-full flex items-start gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors',
                    isSel && 'bg-[var(--jewellery-bg)]/40',
                  )}
                  data-testid={`${testId}-option-${v}`}
                >
                  <Check
                    size={14}
                    className={cn(
                      'mt-1 flex-shrink-0',
                      isSel ? 'text-[var(--jewellery-icon)]' : 'invisible',
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[var(--ink-navy)] truncate">
                      {getLabel(it)}
                    </div>
                    {itemToSubLabel ? (
                      <div className="text-[10px] text-slate-500 truncate">
                        {itemToSubLabel(it)}
                      </div>
                    ) : null}
                  </div>
                </button>
              );
            })
          )}
        </div>
        {footer ? (
          <div className="border-t border-[var(--divider)] p-2 bg-slate-50/60">
            {footer}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export default SearchableSelect;
